/* Tin & Tinder retro kit — progressive enhancement only.
   Everything works without JS. Motion rules for smooth navigation:
   - only transform/opacity are animated (no layout thrash)
   - one rAF-throttled, passive scroll loop shared by every effect
   - IntersectionObserver starts work only when a section is on screen
   - pointer effects only for fine pointers; all motion off under reduced-motion */
(function () {
  if (window.__ttrInit) return;
  window.__ttrInit = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var hasIO = 'IntersectionObserver' in window;
  var motionOK = function () { return !reduceMotion.matches; };
  var toArray = function (list) { return Array.prototype.slice.call(list); };

  /* ---------- Shared scroll loop ---------- */
  var scrollTasks = [];
  var lastY = window.scrollY;
  var ticking = false;
  var docMax = 1;

  var measure = function () {
    docMax = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
  };
  measure();
  window.addEventListener('resize', measure, { passive: true });
  window.addEventListener('load', measure);

  var runScroll = function () {
    var y = window.scrollY;
    var dy = y - lastY;
    lastY = y;
    // A task returns false once its element has left the DOM (Theme Editor reloads)
    for (var i = scrollTasks.length - 1; i >= 0; i--) {
      if (scrollTasks[i](y, dy) === false) scrollTasks.splice(i, 1);
    }
    ticking = false;
  };
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(runScroll);
  }, { passive: true });

  var addScrollTask = function (fn) {
    scrollTasks.push(fn);
    fn(window.scrollY, 0);
  };

  /* ---------- Scroll reveal ---------- */
  var revealIO = hasIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      revealIO.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }) : null;

  function initReveal(scope) {
    scope.querySelectorAll('[data-ttr-reveal]:not(.is-in)').forEach(function (el) {
      if (!revealIO || !motionOK()) el.classList.add('is-in');
      else revealIO.observe(el);
    });
  }

  /* ---------- Header: solid on scroll + reading progress ---------- */
  function initHeader(scope) {
    var header = scope.querySelector('[data-ttr-header]');
    if (!header || header.__ttr) return;
    header.__ttr = true;

    var setHeight = function () {
      document.documentElement.style.setProperty('--ttr-header-h', header.offsetHeight + 'px');
    };
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);

    var bar = header.querySelector('[data-ttr-progress]');
    addScrollTask(function (y) {
      if (!header.isConnected) return false;
      header.classList.toggle('is-scrolled', y > 24);
      if (bar) bar.style.transform = 'scaleX(' + Math.min(1, y / docMax).toFixed(4) + ')';
    });

    /* Close the mobile menu on Escape and return focus to its toggle */
    var menu = header.querySelector('[data-ttr-menu]');
    if (menu) {
      menu.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && menu.open) {
          menu.open = false;
          menu.querySelector('summary').focus();
        }
      });
      menu.addEventListener('toggle', function () {
        document.documentElement.classList.toggle('ttr-menu-open', menu.open);
      });
    }
  }

  /* ---------- Announcement rotation ---------- */
  function initAnnouncement(scope) {
    var bar = scope.querySelector('[data-ttr-rotate]');
    if (!bar || bar.__ttr) return;
    bar.__ttr = true;

    var items = bar.querySelectorAll('[data-ttr-rotate-item]');
    if (items.length < 2 || !motionOK()) return;

    bar.classList.add('is-rotating');
    var index = 0;
    var paused = false;
    var delay = (parseInt(bar.getAttribute('data-ttr-rotate'), 10) || 5) * 1000;

    items.forEach(function (item, i) { item.hidden = i !== 0; });

    ['mouseenter', 'focusin'].forEach(function (ev) {
      bar.addEventListener(ev, function () { paused = true; });
    });
    ['mouseleave', 'focusout'].forEach(function (ev) {
      bar.addEventListener(ev, function () { paused = false; });
    });

    var timer = setInterval(function () {
      if (!bar.isConnected) return clearInterval(timer);
      if (paused || document.hidden) return;
      items[index].hidden = true;
      index = (index + 1) % items.length;
      items[index].hidden = false;
    }, delay);
  }

  /* ---------- Hero: pointer parallax ---------- */
  function initParallax(scope) {
    if (!finePointer.matches || !motionOK()) return;
    scope.querySelectorAll('[data-ttr-parallax]').forEach(function (el) {
      if (el.__ttr) return;
      el.__ttr = true;
      var raf = 0;
      var px = 0;
      var py = 0;
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        px = ((e.clientX - r.left) / r.width - 0.5) * 2;
        py = ((e.clientY - r.top) / r.height - 0.5) * 2;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          el.style.setProperty('--px', px.toFixed(3));
          el.style.setProperty('--py', py.toFixed(3));
        });
      });
      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--px', 0);
        el.style.setProperty('--py', 0);
      });
    });
  }

  /* ---------- Tilt + chrome glint (product cards, framed images) ---------- */
  function initTilt(scope) {
    if (!finePointer.matches || !motionOK()) return;
    scope.querySelectorAll('[data-ttr-tilt]').forEach(function (el) {
      if (el.__ttrTilt) return;
      el.__ttrTilt = true;
      var raf = 0;
      var x = 0.5;
      var y = 0.5;
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        x = (e.clientX - r.left) / r.width;
        y = (e.clientY - r.top) / r.height;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          el.style.setProperty('--tx', (x - 0.5).toFixed(3));
          el.style.setProperty('--ty', (y - 0.5).toFixed(3));
          el.style.setProperty('--gx', (x * 100).toFixed(1) + '%');
          el.classList.add('is-tilting');
        });
      });
      el.addEventListener('pointerleave', function () {
        cancelAnimationFrame(raf);
        raf = 0;
        el.classList.remove('is-tilting');
        el.style.setProperty('--tx', 0);
        el.style.setProperty('--ty', 0);
      });
    });
  }

  /* ---------- Marquee: follows scroll speed and direction ---------- */
  function initMarquee(scope) {
    if (!motionOK()) return;
    scope.querySelectorAll('[data-ttr-marquee]').forEach(function (m) {
      if (m.__ttr) return;
      m.__ttr = true;
      var track = m.querySelector('.ttr-marquee__track');
      if (!track || !track.getAnimations) return;

      // The configured direction lives in CSS; scrolling up flips it, scrolling fast speeds it up
      var dir = 1;
      var boost = 0;
      var raf = 0;
      var apply = function () {
        var anim = track.getAnimations()[0];
        if (!anim) return;
        var rate = dir * (1 + boost);
        if (anim.updatePlaybackRate) anim.updatePlaybackRate(rate);
        else anim.playbackRate = rate;
      };
      var loop = function () {
        raf = 0;
        boost *= 0.9;
        if (boost < 0.02) boost = 0;
        apply();
        if (boost) raf = requestAnimationFrame(loop);
      };
      addScrollTask(function (y, dy) {
        if (!m.isConnected) return false;
        if (!dy) return;
        dir = dy > 0 ? 1 : -1;
        boost = Math.min(4, boost + Math.abs(dy) / 45);
        if (!raf) raf = requestAnimationFrame(loop);
      });
    });
  }

  /* ---------- Carousel: buttons, mouse drag, progress ---------- */
  function initCarousels(scope) {
    scope.querySelectorAll('[data-ttr-carousel]').forEach(function (carousel) {
      if (carousel.__ttr) return;
      carousel.__ttr = true;

      var track = carousel.querySelector('[data-ttr-track]');
      var prev = carousel.querySelector('[data-ttr-prev]');
      var next = carousel.querySelector('[data-ttr-next]');
      var progress = carousel.querySelector('[data-ttr-carousel-progress]');
      if (!track) return;

      var step = function () {
        var slide = track.querySelector('[data-ttr-slide]');
        if (!slide) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var perView = Math.max(1, Math.floor((track.clientWidth + gap) / (slide.offsetWidth + gap)));
        return perView * (slide.offsetWidth + gap);
      };

      var rafUpdate = 0;
      var update = function () {
        rafUpdate = 0;
        var max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        if (progress) {
          var ratio = track.scrollWidth ? (track.scrollLeft + track.clientWidth) / track.scrollWidth : 1;
          progress.style.transform = 'scaleX(' + Math.min(1, ratio).toFixed(4) + ')';
        }
      };
      var queueUpdate = function () {
        if (!rafUpdate) rafUpdate = requestAnimationFrame(update);
      };

      var go = function (dir) {
        track.scrollBy({ left: dir * step(), behavior: motionOK() ? 'smooth' : 'auto' });
      };

      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });
      track.addEventListener('scroll', queueUpdate, { passive: true });
      window.addEventListener('resize', queueUpdate, { passive: true });
      update();

      /* Mouse drag-to-scroll (touch already scrolls natively) */
      var down = false;
      var moved = false;
      var startX = 0;
      var startLeft = 0;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true;
        moved = false;
        startX = e.clientX;
        startLeft = track.scrollLeft;
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) {
          moved = true;
          track.classList.add('is-dragging');
        }
        if (moved) track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!down) return;
        down = false;
        if (moved) track.classList.remove('is-dragging');
      });
      // A drag must not also count as a click on a product link
      track.addEventListener('click', function (e) {
        if (!moved) return;
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }, true);
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    });
  }

  /* ---------- Count-up numbers ---------- */
  function initCount(scope) {
    scope.querySelectorAll('[data-ttr-count]').forEach(function (el) {
      if (el.__ttr) return;
      el.__ttr = true;
      if (!motionOK() || !hasIO) return;

      var finalText = el.textContent.trim();
      var match = finalText.match(/^([^\d]*)(\d[\d,]*(?:\.\d+)?)(.*)$/);
      if (!match) return;
      var prefix = match[1];
      var numStr = match[2];
      var suffix = match[3];
      var target = parseFloat(numStr.replace(/,/g, ''));
      if (!target) return;
      var decimals = (numStr.split('.')[1] || '').length;
      var commas = numStr.indexOf(',') > -1;

      var format = function (v) {
        var s = v.toFixed(decimals);
        if (commas) {
          var parts = s.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          s = parts.join('.');
        }
        return prefix + s + suffix;
      };

      el.textContent = format(0);
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        var start = performance.now();
        var duration = 1600;
        var frame = function (now) {
          var t = Math.min(1, (now - start) / duration);
          var eased = 1 - Math.pow(1 - t, 4);
          el.textContent = t < 1 ? format(target * eased) : finalText;
          if (t < 1) requestAnimationFrame(frame);
        };
        requestAnimationFrame(frame);
      }, { threshold: 0.6 });
      io.observe(el);
    });
  }

  /* ---------- Feature list: active item follows scroll, hover and focus ---------- */
  function initFeatures(scope) {
    scope.querySelectorAll('[data-ttr-features]').forEach(function (wrap) {
      if (wrap.__ttr) return;
      wrap.__ttr = true;

      var items = toArray(wrap.querySelectorAll('[data-ttr-feature]'));
      var media = toArray(wrap.querySelectorAll('[data-ttr-feature-media]'));
      if (!items.length) return;

      var activate = function (index) {
        items.forEach(function (item, i) {
          item.classList.toggle('is-active', i === index);
          var btn = item.querySelector('button');
          if (btn) btn.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
        if (!media.length) return;
        var target = media.filter(function (m) { return m.getAttribute('data-ttr-feature-media') === String(index); })[0] ||
          media.filter(function (m) { return m.getAttribute('data-ttr-feature-media') === 'default'; })[0];
        media.forEach(function (m) { m.classList.toggle('is-active', m === target); });
      };

      items.forEach(function (item, i) {
        item.addEventListener('pointerenter', function () { activate(i); });
        item.addEventListener('focusin', function () { activate(i); });
      });

      if (hasIO) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) activate(items.indexOf(entry.target));
          });
        }, { rootMargin: '-45% 0px -45% 0px' });
        items.forEach(function (item) { io.observe(item); });
      }
      activate(0);
    });
  }

  /* ---------- Gallery lightbox ---------- */
  function initLightbox(scope) {
    scope.querySelectorAll('[data-ttr-gallery]').forEach(function (gallery) {
      if (gallery.__ttr) return;
      gallery.__ttr = true;

      var dialog = gallery.querySelector('[data-ttr-lightbox]');
      var openers = toArray(gallery.querySelectorAll('[data-ttr-lightbox-open]'));
      if (!dialog || typeof dialog.showModal !== 'function' || !openers.length) return;

      var img = dialog.querySelector('[data-ttr-lightbox-img]');
      var caption = dialog.querySelector('[data-ttr-lightbox-caption]');
      var counter = dialog.querySelector('[data-ttr-lightbox-count]');
      var index = 0;
      var opener = null;

      if (openers.length < 2) dialog.classList.add('is-single');

      var show = function (i) {
        index = (i + openers.length) % openers.length;
        var src = openers[index].dataset;
        dialog.classList.remove('is-ready');
        img.onload = function () { dialog.classList.add('is-ready'); };
        img.src = src.full;
        img.alt = src.alt || '';
        if (img.complete) dialog.classList.add('is-ready');

        caption.textContent = '';
        if (src.handle) {
          var h = document.createElement('strong');
          h.textContent = '@' + src.handle;
          caption.appendChild(h);
        }
        if (src.caption) {
          var c = document.createElement('span');
          c.textContent = src.caption;
          caption.appendChild(c);
        }
        if (src.link) {
          var a = document.createElement('a');
          a.href = src.link;
          a.target = '_blank';
          a.rel = 'noopener';
          a.textContent = 'View post ↗';
          caption.appendChild(a);
        }
        if (counter) counter.textContent = (index + 1) + ' / ' + openers.length;
      };

      openers.forEach(function (btn, i) {
        btn.addEventListener('click', function () {
          opener = btn;
          show(i);
          dialog.showModal();
          document.documentElement.classList.add('ttr-lightbox-open');
        });
      });

      dialog.addEventListener('click', function (e) {
        var t = e.target;
        if (t === dialog) return dialog.close();
        if (t.closest('[data-ttr-lightbox-close]')) return dialog.close();
        if (t.closest('[data-ttr-lightbox-prev]')) return show(index - 1);
        if (t.closest('[data-ttr-lightbox-next]')) return show(index + 1);
      });
      dialog.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); show(index - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); show(index + 1); }
      });
      dialog.addEventListener('close', function () {
        document.documentElement.classList.remove('ttr-lightbox-open');
        if (opener) opener.focus();
      });
    });
  }

  /* ---------- Post carousel (gallery): one post snaps into place at a time ---------- */
  function initPosts(scope) {
    scope.querySelectorAll('[data-ttr-posts]').forEach(function (root) {
      if (root.__ttr) return;
      root.__ttr = true;

      var track = root.querySelector('[data-ttr-posts-track]');
      var cards = track ? toArray(track.querySelectorAll('[data-ttr-post]')) : [];
      var prev = root.querySelector('[data-ttr-posts-prev]');
      var next = root.querySelector('[data-ttr-posts-next]');
      var live = root.querySelector('[data-ttr-posts-live]');
      if (!cards.length) return;

      var active = -1;
      var announced = false;

      // Scroll position that centres card i (the track is the offset parent)
      var target = function (i) {
        var c = cards[i];
        return c.offsetLeft - (track.clientWidth - c.offsetWidth) / 2;
      };
      var nearest = function () {
        var mid = track.scrollLeft + track.clientWidth / 2;
        var best = 0;
        var bestDist = Infinity;
        cards.forEach(function (c, i) {
          var d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        return best;
      };
      var setActive = function (i) {
        if (i === active) return;
        active = i;
        cards.forEach(function (c, j) { c.classList.toggle('is-active', j === i); });
        if (prev) prev.disabled = i === 0;
        if (next) next.disabled = i === cards.length - 1;
        // Announce only after the customer has started moving the carousel
        if (live && announced) live.textContent = 'Post ' + (i + 1) + ' of ' + cards.length;
      };
      var go = function (i) {
        i = Math.max(0, Math.min(cards.length - 1, i));
        announced = true;
        track.scrollTo({ left: target(i), behavior: motionOK() ? 'smooth' : 'auto' });
      };

      // Posts can differ in width (each photo keeps its own shape), so the end
      // padding is measured so the first and last post can sit dead centre
      var mobile = window.matchMedia('(max-width: 749px)');
      var pad = function () {
        if (mobile.matches) {
          track.style.paddingLeft = '';
          track.style.paddingRight = '';
          return;
        }
        var first = cards[0];
        var last = cards[cards.length - 1];
        track.style.paddingLeft = Math.max(16, (track.clientWidth - first.offsetWidth) / 2) + 'px';
        track.style.paddingRight = Math.max(16, (track.clientWidth - last.offsetWidth) / 2) + 'px';
      };
      pad();

      var raf = 0;
      track.addEventListener('scroll', function () {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          setActive(nearest());
        });
      }, { passive: true });
      var onResize = function () {
        var keep = active;
        pad();
        if (keep > -1) track.scrollTo({ left: target(keep), behavior: 'auto' });
        setActive(nearest());
      };
      if ('ResizeObserver' in window) new ResizeObserver(onResize).observe(track);
      else window.addEventListener('resize', onResize, { passive: true });

      if (prev) prev.addEventListener('click', function () { go(active - 1); });
      if (next) next.addEventListener('click', function () { go(active + 1); });

      // Arrow keys move one full post; Home/End jump to the ends
      track.addEventListener('keydown', function (e) {
        var map = { ArrowLeft: active - 1, ArrowRight: active + 1, Home: 0, End: cards.length - 1 };
        if (!(e.key in map)) return;
        if (e.target !== track && /INPUT|TEXTAREA/.test(e.target.tagName)) return;
        e.preventDefault();
        go(map[e.key]);
      });

      // Tabbing into a side post centres it; clicking a side post brings it forward
      cards.forEach(function (card, i) {
        card.addEventListener('focusin', function () { if (i !== active) go(i); });
        card.addEventListener('click', function (e) {
          if (i === active || dragMoved) return;
          e.preventDefault();
          go(i);
        });
      });

      /* Mouse drag (touch and trackpads scroll natively; Shift + wheel is native too) */
      var down = false;
      var dragMoved = false;
      var startX = 0;
      var startLeft = 0;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true;
        dragMoved = false;
        startX = e.clientX;
        startLeft = track.scrollLeft;
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (!dragMoved && Math.abs(dx) > 6) {
          dragMoved = true;
          track.classList.add('is-dragging');
        }
        if (dragMoved) track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!down) return;
        down = false;
        if (!dragMoved) return;
        track.classList.remove('is-dragging');
        go(nearest());
        setTimeout(function () { dragMoved = false; }, 0);
      });
      track.addEventListener('click', function (e) {
        if (!dragMoved) return;
        e.preventDefault();
        e.stopPropagation();
      }, true);
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });

      /* Save-to-favourites heart: this visitor's own choice, remembered on this device */
      toArray(root.querySelectorAll('[data-ttr-like]')).forEach(function (btn) {
        var key = 'ttr-like-' + btn.getAttribute('data-ttr-like');
        try { if (localStorage.getItem(key) === '1') btn.setAttribute('aria-pressed', 'true'); } catch (err) {}
        btn.addEventListener('click', function () {
          var on = btn.getAttribute('aria-pressed') !== 'true';
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          if (on && motionOK()) {
            btn.classList.remove('is-popping');
            void btn.offsetWidth;
            btn.classList.add('is-popping');
          }
          try { if (on) localStorage.setItem(key, '1'); else localStorage.removeItem(key); } catch (err) {}
        });
      });

      /* Share: native share sheet where available, otherwise copy the link */
      toArray(root.querySelectorAll('[data-ttr-share]')).forEach(function (btn) {
        var status = btn.parentNode.querySelector('[data-ttr-share-status]');
        var say = function (msg) {
          if (!status) return;
          status.textContent = msg;
          setTimeout(function () { status.textContent = ''; }, 2200);
        };
        btn.addEventListener('click', function () {
          var url = new URL(btn.getAttribute('data-url') || location.href, location.href).href;
          var title = btn.getAttribute('data-title') || document.title;
          if (navigator.share) {
            navigator.share({ title: title, url: url }).catch(function () {});
          } else if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(function () { say('Link copied'); }, function () { say(url); });
          } else {
            say(url);
          }
        });
      });

      setActive(nearest());
    });
  }

  function init(scope) {
    scope = scope || document;
    initPosts(scope);
    initReveal(scope);
    initHeader(scope);
    initAnnouncement(scope);
    initParallax(scope);
    initTilt(scope);
    initMarquee(scope);
    initCarousels(scope);
    initCount(scope);
    initFeatures(scope);
    initLightbox(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Re-init when sections are added or changed in the Theme Editor */
  document.addEventListener('shopify:section:load', function (e) {
    measure();
    init(e.target);
  });
})();
